import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listJobs, listSyncAccounts, runJob, joinChannels, cancelJob, isJobActive,
  JobPayload, RunnableJobType,
} from '@/api/sync';

// Jobs are polled, not streamed: fast while anything is queued/running, lazy
// once the queue is idle so a parked tab costs one request every 30 s.
const ACTIVE_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;

export const SYNC_JOBS_KEY = ['sync-jobs'];
export const SYNC_ACCOUNTS_KEY = ['sync-accounts'];

// `accountId` is the 4th arg so the existing positional 1/2/3-arg callers
// (SyncPage, SettingsPage) keep working; undefined means "all accounts".
export const useJobs = (jobType?: string, limit = 50, enabled = true, accountId?: number) =>
  useQuery({
    queryKey: [...SYNC_JOBS_KEY, 'list', jobType ?? null, limit, accountId ?? null],
    queryFn: () => listJobs(jobType, limit, accountId),
    enabled,
    refetchInterval: (query) =>
      query.state.data?.some(isJobActive) ? ACTIVE_POLL_MS : IDLE_POLL_MS,
  });

// Channel counts are recorded by the worker in the allowlist, not read live
// from Telegram — a minute-stale number is accurate enough, so no polling.
export const useSyncAccounts = (enabled = true) =>
  useQuery({
    queryKey: SYNC_ACCOUNTS_KEY,
    queryFn: listSyncAccounts,
    enabled,
    staleTime: 60_000,
  });

export const useRunJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobType, params }: { jobType: RunnableJobType; params?: JobPayload }) =>
      runJob(jobType, params),
    onSuccess: () => qc.invalidateQueries({ queryKey: SYNC_JOBS_KEY }),
  });
};

export const useJoinChannels = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ links, accountId }: { links: string[]; accountId: number }) =>
      joinChannels(links, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SYNC_JOBS_KEY }),
  });
};

export const useCancelJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (jobId: number) => cancelJob(jobId),
    onSuccess: () => qc.invalidateQueries({ queryKey: SYNC_JOBS_KEY }),
  });
};
